import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp } from "lucide-react";

interface MonthlyTrend {
  month: string;
  posts: number;
  engagement: number;
}

interface PostingPatternsTimelineProps {
  timeline: MonthlyTrend[];
}

export const PostingPatternsTimeline = ({ timeline }: PostingPatternsTimelineProps) => {
  if (!timeline || timeline.length === 0) {
    return null;
  }

  // Format month labels (YYYY-MM -> MMM YY)
  const formattedData = timeline.map(item => {
    const [year, month] = item.month.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const shortMonth = monthNames[parseInt(month) - 1];
    
    return {
      ...item,
      monthLabel: `${shortMonth} ${year.slice(2)}`
    };
  });

  // Calculate trends
  const calculateTrend = (data: number[]) => {
    if (data.length < 2) return 0;
    const recent = data.slice(-3).reduce((a, b) => a + b, 0) / Math.min(3, data.length);
    const older = data.slice(0, -3).reduce((a, b) => a + b, 0) / Math.max(1, data.length - 3);
    return older === 0 ? 0 : ((recent - older) / older) * 100;
  };

  const postsTrend = calculateTrend(formattedData.map(d => d.posts));
  const engagementTrend = calculateTrend(formattedData.map(d => d.engagement));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-semibold mb-2">{payload[0].payload.monthLabel}</p>
          <p className="text-sm text-primary">
            Posts: <span className="font-semibold">{payload[0].value}</span>
          </p>
          <p className="text-sm text-secondary">
            Engagement: <span className="font-semibold">{payload[1].value.toLocaleString()}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <CardTitle>Trendy Publikacji w Czasie</CardTitle>
            </div>
            <CardDescription>
              Liczba postów i zaangażowanie w ostatnich miesiącach
            </CardDescription>
          </div>
          
          <div className="flex gap-4 text-sm">
            <div className="text-right">
              <p className="text-muted-foreground">Trend postów</p>
              <p className={`font-bold ${postsTrend >= 0 ? 'text-success' : 'text-destructive'}`}>
                {postsTrend >= 0 ? '+' : ''}{postsTrend.toFixed(1)}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-muted-foreground">Trend zaangażowania</p>
              <p className={`font-bold ${engagementTrend >= 0 ? 'text-success' : 'text-destructive'}`}>
                {engagementTrend >= 0 ? '+' : ''}{engagementTrend.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={formattedData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="monthLabel" 
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              yAxisId="left"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'Posts', angle: -90, position: 'insideLeft' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'Engagement', angle: 90, position: 'insideRight' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="posts"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--primary))', r: 4 }}
              name="Posts"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="engagement"
              stroke="hsl(var(--secondary))"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--secondary))', r: 4 }}
              name="Engagement"
            />
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">
              {formattedData.reduce((sum, d) => sum + d.posts, 0)}
            </p>
            <p className="text-xs text-muted-foreground">Total Posts</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">
              {Math.round(formattedData.reduce((sum, d) => sum + d.posts, 0) / formattedData.length)}
            </p>
            <p className="text-xs text-muted-foreground">Avg Posts/Month</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <p className="text-2xl font-bold">
              {Math.round(formattedData.reduce((sum, d) => sum + d.engagement, 0) / formattedData.length).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">Avg Engagement/Month</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
